// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback } from 'react';
import { generateHexString } from '../utils/generate-hex-string';
import { environment } from '@shared/environment';

interface WealthReaderConfig {
  entitiesToDisplay?: string[];

  defaultLogin?: string;

  defaultLoginEntityCountry?: string;

  oneShot?: boolean;
}

export const useWealthReaderConnection = () => {
  const generateHex = useCallback(() => generateHexString(), []);

  const calculateSha256 = useCallback(
    async (inputString: string): Promise<string> => {
      const encoder = new TextEncoder();
      const data = encoder.encode(inputString);
      const buffer = await crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    },
    [],
  );

  const handleWealthReaderConnection = useCallback(
    async (
      conf: WealthReaderConfig = { entitiesToDisplay: [], oneShot: false },
    ) => {
      const codeVerifier = generateHex();
      const nonce = generateHex();
      const state = generateHex();
      const challengeCode = await calculateSha256(codeVerifier);
      const wrConfObject = {
        wait_full_response: true,
        product_types: conf.oneShot
          ? 'accounts,portfolios,loans,cards'
          : 'accounts',
        only_balances: true,
        ...(conf.entitiesToDisplay && {
          entities_to_display: conf.entitiesToDisplay,
        }),
        ...(conf.defaultLogin && {
          default_login: conf.defaultLogin,
        }),
        ...(conf.defaultLoginEntityCountry && {
          default_login_entity_country: conf.defaultLoginEntityCountry,
        }),
      };
      const wrConfHex = Buffer.from(JSON.stringify(wrConfObject)).toString('hex');

      const params = {
        challenge_code: challengeCode,
        code_challenge_method: 'S256',
        redirect_uri: environment.SERVICES.WEALTH_READER_REDIRECT_URL!,
        response_type: 'code',
        state,
        nonce,
        wr_conf: wrConfHex,
      };

      const queryParams = new URLSearchParams(params);
      const codeUri = `https://oauth.wealthreader.com/oauth2/?${queryParams}`;
      localStorage.setItem('code_verifier', codeVerifier);
      localStorage.setItem('nonce', nonce);
      window.location.href = codeUri;
    },
    [generateHex, calculateSha256],
  );

  return {
    handleWealthReaderConnection,
  };
};
